import { useState, useEffect } from 'react';
import { isEmpty } from 'lodash';

import CardDetails from 'pages/dashboards/v5/components/CardDetails';

import vendor from 'assets/images/vendor.png';

function useServicesAdd({
    darkMode,
    isAdding,
    isServiceProfileSelected,
    isCreatingServiceError,
    isCurrentServiceAdding,
    serviceProfile,
    serviceWizard,
    vendorDefined,
}) {
    const [showError, setShowError] = useState(false);

    const isOperatorPlatform =
        serviceProfile?.serviceType === 'OperatorPlatform';
    // Simulate loading delay before showing error state without changing useServiceWizard
    useEffect(() => {
        if (isCreatingServiceError) {
            const timeout = setTimeout(() => {
                setShowError(true);
            }, 500);
            return () => clearTimeout(timeout);
        } else {
            setShowError(false);
        }
    }, [isCreatingServiceError]);

    // Get the image source for VendorDefined services
    const getVendorDefinedImageSrc = () => {
        const logoLarge = serviceProfile.serviceDefinition?.logoLarge;

        if (isEmpty(logoLarge)) {
            return vendor;
        }

        if (import.meta.env.MODE === 'development') {
            return `${import.meta.env.VITE_APP_IMAGE_BASE}/images/${logoLarge}`;
        }

        return `${window.location.origin}/images/${logoLarge}`;
    };

    // Get the image source for services
    const getImageSrc = () => {
        if (vendorDefined) {
            return getVendorDefinedImageSrc();
        }
        if (isOperatorPlatform) {
            return (
                `${import.meta.env.VITE_APP_IMAGE_BASE}/images/uploads/${serviceProfile?.customLogo}` ||
                getVendorDefinedImageSrc()
            );
        }
        return CardDetails(serviceProfile.serviceType)?.icon;
    };

    const getServiceName = () => {
        if (vendorDefined) {
            return serviceProfile.serviceDefinition?.name;
        }
        if (isOperatorPlatform) {
            return serviceProfile?.customDisplayName;
        }
        return CardDetails(serviceProfile.serviceType)?.serviceName;
    };

    const getServiceDescription = () => {
        if (vendorDefined) {
            return {
                short: serviceProfile.serviceDefinition?.descriptionShort,
                long: serviceProfile.serviceDefinition?.descriptionLong,
            };
        }
        if (isOperatorPlatform) {
            return {
                short: serviceProfile?.customDescription,
            };
        }
        return {
            short: CardDetails(serviceProfile.serviceType)?.addDescription,
            long: null,
        };
    };

    const getCardBackground = () => {
        if (showError) {
            if (darkMode) {
                return 'rgba(254, 178, 178, 0.16)';
            }
            return 'red.50';
        }
        if (darkMode) {
            return 'dark.tcap';
        }
        return null;
    };

    const getCardBorder = () => {
        if (showError) {
            return '1px solid var(--chakra-colors-red-500)';
        }
        // (3037) White tiles need an edge to stand out from the light page.
        if (!darkMode) {
            return '1px solid var(--chakra-colors-gray-200)';
        }
        return 'none';
    };

    const getPointerEvents = () => {
        if (serviceWizard.isCreatingService && !showError) {
            return 'none';
        }
        return 'auto';
    };

    const getFilterEffect = () => {
        if (showError) {
            return 'none';
        }
        if (serviceWizard.isCreatingService && !isServiceProfileSelected) {
            return 'blur(2px)';
        }
        return null;
    };

    const getOpacity = () => {
        if ((isCurrentServiceAdding || isAdding) && !showError) {
            return 0.7;
        }
        return 1;
    };

    const cardBackground = getCardBackground();
    const cardBorder = getCardBorder();
    const pointerEvents = getPointerEvents();
    const filterEffect = getFilterEffect();
    const opacity = getOpacity();
    const imageSrc = getImageSrc();
    const serviceName = getServiceName();
    const serviceDescription = getServiceDescription();

    return {
        cardBackground,
        cardBorder,
        pointerEvents,
        filterEffect,
        opacity,
        imageSrc,
        serviceName,
        serviceDescription,
        showError,
        setShowError,
    };
}

export default useServicesAdd;
